import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  CircularProgress,
  Grid,
  IconButton,
  Avatar,
  Switch,
  FormControlLabel,
} from "@material-ui/core";
import { useState, useEffect } from "react";
import { ShopDetailsResponseModel, EditShopDetailsRequestModel } from "../redux/actions/shopDetails";
import CloseIcon from "@material-ui/icons/Close";

export interface EditShopData {
  PhoneNumber?: string;
  InfoEmail?: string;
  Name?: string;
  ShopImageBase64?: string;
  Address?: {
    Line1?: string;
    Line2?: string;
    Line3?: string;
    PostalCode?: string;
    County?: string;
    City?: string;
    Lat?: number;
    Long?: number;
  };
  TkdEnabled?: boolean;
  MarketplaceEnabled?: boolean;
  OrkestroDisplayName?: string;
}

interface EditShopModalProps {
  visible: boolean;
  onClose: () => void;
  onSubmit: (data: EditShopDetailsRequestModel) => void;
  shop?: ShopDetailsResponseModel;
  loading?: boolean;
}

export const EditShopModal = ({
  visible,
  onClose,
  onSubmit,
  shop,
  loading = false
}: EditShopModalProps) => {
  const [formData, setFormData] = useState<EditShopData>({
    PhoneNumber: '',
    InfoEmail: '',
    Name: '',
    ShopImageBase64: '',
    Address: {
      Line1: '',
      Line2: '',
      Line3: '',
      PostalCode: '',
      County: '',
      City: '',
    },
    TkdEnabled: false,
    MarketplaceEnabled: false,
    OrkestroDisplayName: '',
  });

  const [imagePreview, setImagePreview] = useState<string>('');
  const [imageChanged, setImageChanged] = useState<boolean>(false);

  useEffect(() => {
    if (shop) {
      setFormData({
        PhoneNumber: shop.PhoneNumber || '',
        InfoEmail: shop.InfoEmail || '',
        Name: shop.Name || '',
        ShopImageBase64: '', // Don't send existing image URL as base64
        Address: {
          Line1: shop.Address.Line1 || '',
          Line2: shop.Address.Line2 || '',
          Line3: shop.Address.Line3 || '',
          PostalCode: shop.Address.PostalCode || '',
          County: shop.Address.County || '',
          City: shop.Address.City || '',
          Lat: shop.Address.Lat || undefined,
          Long: shop.Address.Long || undefined,
        },
        TkdEnabled: shop.TkdEnabled || false,
        MarketplaceEnabled: shop.MarketplaceEnabled || false,
        OrkestroDisplayName: shop.OrkestroDisplayName || '',
      });
      setImagePreview(shop.ShopImageUrl || '');
      setImageChanged(false);
    }
  }, [shop]);

  const handleInputChange = (field: string, value: string | boolean | number | undefined) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...(prev[parent as keyof EditShopData] as any),
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const result = e.target?.result as string;
        setFormData(prev => ({
          ...prev,
          ShopImageBase64: result
        }));
        setImagePreview(result);
        setImageChanged(true);
      };
      reader.readAsDataURL(file);
    }
  };

  const onFormSubmit = () => {
    const submitData: EditShopDetailsRequestModel = {
      PhoneNumber: formData.PhoneNumber || undefined,
      InfoEmail: formData.InfoEmail || undefined,
      Name: formData.Name || undefined,
      ShopImageBase64: imageChanged ? formData.ShopImageBase64 : undefined,
      Address: formData.Address,
      TkdEnabled: formData.TkdEnabled,
      MarketplaceEnabled: formData.MarketplaceEnabled,
      OrkestroDisplayName: formData.OrkestroDisplayName || undefined,
    };

    onSubmit(submitData);
  };

  const handleClose = () => {
    onClose();
  };

  return (
    <Dialog
      open={visible}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        style: {
          borderRadius: 12,
        },
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Edit Shop</Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <Box style={{ padding: '20px 0' }}>
          <Grid container spacing={3}>
            {/* Shop Image Upload Section */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom>
                Shop Image
              </Typography>
              <Box display="flex" alignItems="center" style={{ gap: 16 }}>
                <Avatar
                  src={imagePreview}
                  style={{ width: 80, height: 80 }}
                >
                  {shop?.Name?.charAt(0).toUpperCase()}
                </Avatar>
                <Box>
                  <input
                    accept="image/*"
                    style={{ display: 'none' }}
                    id="shop-image-upload"
                    type="file"
                    onChange={handleImageUpload}
                  />
                  <label htmlFor="shop-image-upload">
                    <Button variant="outlined" component="span">
                      Upload Image
                    </Button>
                  </label>
                  <Typography variant="caption" display="block" color="textSecondary">
                    Recommended size: 1200x900px
                  </Typography>
                </Box>
              </Box>
            </Grid>

            {/* Basic Information */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Shop Name"
                value={formData.Name}
                onChange={(e) => handleInputChange('Name', e.target.value)}
                variant="outlined"
                margin="normal"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Phone Number"
                value={formData.PhoneNumber}
                onChange={(e) => handleInputChange('PhoneNumber', e.target.value)}
                variant="outlined"
                margin="normal"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Info Email"
                value={formData.InfoEmail}
                onChange={(e) => handleInputChange('InfoEmail', e.target.value)}
                variant="outlined"
                margin="normal"
                type="email"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Nash Business Name"
                value={formData.OrkestroDisplayName}
                onChange={(e) => handleInputChange('OrkestroDisplayName', e.target.value)}
                variant="outlined"
                margin="normal"
                helperText="Used as Business Name in Nash implementation"
              />
            </Grid>

            {/* Address Section */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom style={{ marginTop: 16 }}>
                Address
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address Line 1"
                value={formData.Address?.Line1 || ''}
                onChange={(e) => handleInputChange('Address.Line1', e.target.value)}
                variant="outlined"
                margin="normal"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address Line 2"
                value={formData.Address?.Line2 || ''}
                onChange={(e) => handleInputChange('Address.Line2', e.target.value)}
                variant="outlined"
                margin="normal"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address Line 3"
                value={formData.Address?.Line3 || ''}
                onChange={(e) => handleInputChange('Address.Line3', e.target.value)}
                variant="outlined"
                margin="normal"
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Postal Code"
                value={formData.Address?.PostalCode || ''}
                onChange={(e) => handleInputChange('Address.PostalCode', e.target.value)}
                variant="outlined"
                margin="normal"
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="City"
                value={formData.Address?.City || ''}
                onChange={(e) => handleInputChange('Address.City', e.target.value)}
                variant="outlined"
                margin="normal"
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="County"
                value={formData.Address?.County || ''}
                onChange={(e) => handleInputChange('Address.County', e.target.value)}
                variant="outlined"
                margin="normal"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Latitude"
                type="number"
                value={formData.Address?.Lat || ''}
                onChange={(e) => handleInputChange('Address.Lat', parseFloat(e.target.value) || undefined)}
                variant="outlined"
                margin="normal"
                inputProps={{ step: "any" }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Longitude"
                type="number"
                value={formData.Address?.Long || ''}
                onChange={(e) => handleInputChange('Address.Long', parseFloat(e.target.value) || undefined)}
                variant="outlined"
                margin="normal"
                inputProps={{ step: "any" }}
              />
            </Grid>

            {/* Settings Section */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom style={{ marginTop: 16 }}>
                Settings
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.TkdEnabled || false}
                    onChange={(e) => handleInputChange('TkdEnabled', e.target.checked)}
                    color="primary"
                  />
                }
                label="TKD Enabled"
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.MarketplaceEnabled || false}
                    onChange={(e) => handleInputChange('MarketplaceEnabled', e.target.checked)}
                    color="primary"
                  />
                }
                label="Marketplace Enabled"
              />
            </Grid>
          </Grid>
        </Box>
      </DialogContent>

      <DialogActions style={{ padding: '16px 24px' }}>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={onFormSubmit}
          variant="contained"
          color="primary"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} /> : null}
        >
          {loading ? 'Saving...' : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
